<template>
  <div>
    <div v-if="selectedAnimal" class="top-button-row">
      <!-- Bottom save button -->
      <div class="top-button-container">
        <a class="button is-danger" @click="deleteAnimal" :disabled="isSaving">
          Delete
        </a>

        <a
          class="button is-primary"
          @click="saveAnimal"
          :disabled="isSaving || !isDirty"
          :class="{ 'is-loading': isSaving }"
        >
          Save
        </a>
      </div>

      <!-- Validation errors -->
      <div class="notification is-danger" v-if="hasValidationErrors">
        Validation errors:
        <ul>
          <li v-for="validationError in validationErrors" :key="validationError">
            - {{ validationError }}
          </li>
        </ul>
      </div>

      <!-- Save error -->
      <div class="notification is-danger" v-if="saveError">
        {{ saveError }}
      </div>
    </div>

    <div v-if="animalCopy" class="container main">
      <!-- Category section -->
      <div class="box">
        <div class="field">
          <label class="label">Category</label>
          <div class="control">
            <label class="radio">
              <input
                type="radio"
                value="mammal"
                v-model="animalCopy.category"
                :disabled="isSaving"
              />
              Mammal
            </label>
            <label class="radio">
              <input type="radio" value="bird" v-model="animalCopy.category" :disabled="isSaving" />
              Bird
            </label>
          </div>
        </div>
      </div>

      <!-- Animal details section -->
      <div class="box">
        <div class="field">
          <label class="label">Name (English)</label>
          <div class="control">
            <input class="input" type="text" v-model="animalCopy.name" :disabled="isSaving" />
          </div>
          <p class="help is-danger" v-if="!isNameValid">Name is required</p>
        </div>

        <div class="field">
          <label class="label">Icon</label>

          <div class="image-container">
            <div class="image-scroll-area" v-if="!iconLoadFailed">
              <img
                class="iconImage"
                :src="iconImageURL"
                @load="onIconLoaded"
                @error="onIconLoadError"
              />
            </div>

            <p class="image-missing" v-if="iconLoadFailed">[No icon]</p>

            <div class="overlay" v-if="!iconIsLoaded && !iconLoadFailed">
              <Spinner />
            </div>
          </div>

          <div class="file">
            <label class="file-label">
              <input
                class="file-input"
                type="file"
                name="resume"
                @change="uploadIconImage($event.target.files)"
                :disabled="isUploadingIconImage"
              />
              <span class="file-cta">
                <span class="file-icon">
                  <i class="icon-upload" aria-hidden="true"></i>
                </span>
                <span class="file-label">
                  Upload icon…
                </span>
                <Spinner v-if="isUploadingIconImage" width="16px" height="16px" />
              </span>
            </label>
          </div>

          <!-- Image upload error -->
          <div class="notification is-danger" v-if="iconUploadError">
            {{ iconUploadError }}
          </div>
        </div>
      </div>

      <!-- Countries section -->
      <div class="box">
        <!-- <label class="label">Available in Countries</label> -->

        <div class="field">
          <label class="label">Country filter</label>
          <div class="control">
            <div class="select">
              <select v-model="animalCopy.countryFilterEnabled" :disabled="isSaving">
                <option v-bind:value="false">Disabled (animal visible in all countries)</option>
                <option v-bind:value="true">Enabled</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="animalCopy.countryFilterEnabled">
          <div class="field" v-for="countryCode in sortedCountryCodes" :key="countryCode">
            <div class="control">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="countryCode"
                  v-model="animalCopy.countryCodes"
                  :disabled="isSaving"
                />
                {{ countryCode }}
              </label>
            </div>
          </div>
          <div class="field has-addons add-country-code">
            <div class="control">
              <input
                class="input"
                type="text"
                placeholder="Country code"
                v-model="newCountryCode"
                :disabled="isSaving"
              />
            </div>

            <div class="control">
              <a
                class="button is-primary"
                :disabled="!isNewCountryCodeValid || isSaving"
                @click="addCountryCode"
              >
                Add
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Localizations section -->
      <div class="box">
        <label class="label">Localizations</label>

        <div class="is-flex is-flex-direction-row">
          <div class="has-background-white-ter localizations-sidebar">
            <div class="inline-list">
              <a
                v-for="languageCode in sortedLanguageCodes"
                :key="languageCode"
                @click="languageCodeClicked(languageCode)"
                class="panel-block"
                :class="{
                  'has-background-white': !isSelectedLanguageCode(languageCode),
                  'has-background-grey-dark': isSelectedLanguageCode(languageCode),
                  'has-text-light': isSelectedLanguageCode(languageCode)
                }"
              >
                {{ languageCode }}
              </a>
            </div>

            <div class="field has-addons add-country-code">
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="Language code"
                  v-model="newLanguageCode"
                  :disabled="isSaving"
                />
              </div>

              <div class="control">
                <a
                  class="button is-primary"
                  :disabled="!isNewLanguageCodeValid || isSaving"
                  @click="addLanguageCode"
                >
                  Add
                </a>
              </div>
            </div>
          </div>

          <div class="container is-flex-grow-1 inline-list-details" v-if="selectedLocalization">
            <div class="field">
              <label class="label">Name</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="selectedLocalization.name"
                  :disabled="isSaving"
                />
              </div>
              <p class="help is-danger" v-if="!isSelectedLocalizationNameValid">Name is required</p>

              <a
                class="button is-danger is-outlined is-pulled-right"
                :disabled="isSaving"
                @click="removeLocalization"
              >
                Remove localization
              </a>
            </div>
          </div>

          <div
            class="container add-localization"
            v-if="!selectedLocalization && selectedLanguageCode"
          >
            <a class="button is-primary is-outlined" :disabled="isSaving" @click="addLocalization">
              Add localization
            </a>
          </div>
        </div>
      </div>

      <!-- Hunting periods section -->
      <div class="box">
        <label class="label">Hunting Periods</label>

        <div class="field has-addons add-periods-country-code">
          <div class="control">
            <input
              class="input"
              type="text"
              placeholder="Country code"
              v-model="newPeriodsCountryCode"
              :disabled="isSaving"
            />
          </div>

          <div class="control">
            <a
              class="button is-primary"
              :disabled="!isNewPeriodsCountryCodeValid || isSaving"
              @click="addPeriodsCountryCode"
            >
              Add
            </a>
          </div>
        </div>

        <!-- Tabs -->
        <div class="tabs is-centered top-tabs">
          <ul>
            <li
              v-for="countryCode in periodsCountryCodes"
              :key="countryCode"
              :class="{ 'is-active': countryCode === selectedHuntingPeriodsCountryCode }"
              @click="selectHuntingPeriodsCountryCode(countryCode)"
            >
              <a>{{ countryCode }}</a>
            </li>
          </ul>
        </div>

        <!-- Tab content -->
        <div class="is-flex is-flex-direction-row" v-if="selectedHuntingPeriodsCountryCode">
          <div class="has-background-white-ter localizations-sidebar">
            <div class="inline-list">
              <a
                v-for="huntingPeriod in visibleHuntingPeriods"
                :key="huntingPeriod.id"
                @click="huntingPeriodClicked(huntingPeriod)"
                class="panel-block"
                :class="{
                  'has-background-white': !isSelectedHuntingPeriodCode(huntingPeriod),
                  'has-background-grey-dark': isSelectedHuntingPeriodCode(huntingPeriod),
                  'has-text-light': isSelectedHuntingPeriodCode(huntingPeriod)
                }"
              >
                <span>{{ huntingPeriod.from }} - {{ huntingPeriod.to }}</span>
              </a>
            </div>

            <div class="field add-hunting-period">
              <a class="button is-primary" @click="addHuntingPeriod" :disabled="isSaving">
                Add period
              </a>
            </div>
          </div>

          <div class="container is-flex-grow-1 hunting-period-details" v-if="selectedHuntingPeriod">
            <div class="field">
              <label class="label">
                <div class="hunting-period">
                  <span>Period</span>

                  <a
                    class="button is-outlined is-danger is-small"
                    @click="removeHuntingPeriod(selectedHuntingPeriod)"
                    :disabled="isSaving"
                  >
                    Remove period
                  </a>
                </div>
              </label>
              <a class="" @click="editHuntingPeriod(selectedHuntingPeriod)" :disabled="isSaving">
                <span>{{ selectedHuntingPeriod.from }} - {{ selectedHuntingPeriod.to }}</span>
                <span class="icon">
                  <i class="icon-edit-2"></i>
                </span>
              </a>
            </div>

            <div class="field">
              <label class="label">Restrictions</label>
              <div class="control">
                <textarea
                  class="textarea"
                  placeholder="None"
                  v-model="selectedHuntingPeriod.restrictions"
                  :disabled="isSaving"
                />
              </div>
            </div>

            <div class="field">
              <label class="label">Districts</label>
              <div
                class="districts-container"
                v-if="selectableDistricts && selectableDistricts.length > 0"
              >
                <a
                  class="is-size-7"
                  @click="selectAllPeriodDistricts(selectedHuntingPeriod)"
                  :disabled="isSaving"
                >
                  Select all
                </a>

                <a
                  class="is-size-7"
                  @click="deselectAllPeriodDistricts(selectedHuntingPeriod)"
                  :disabled="isSaving"
                >
                  Deselect all
                </a>

                <div
                  class="control district-checkbox"
                  v-for="district in selectableDistricts"
                  :key="district.id"
                >
                  <label class="checkbox">
                    <input
                      type="checkbox"
                      :value="district.id"
                      v-model="selectedHuntingPeriod.districtIds"
                      :disabled="isSaving"
                    />
                    <span class="district-name">{{ district.name }}</span>
                  </label>

                  <a @click="editDistrict(district)" :disabled="isSaving">
                    <span class="icon">
                      <i class="icon-edit-2"></i>
                    </span>
                  </a>
                </div>
              </div>

              <a
                class="add-district button is-outlined is-primary is-small"
                @click="addDistrict()"
                :disabled="isSaving"
              >
                Add district
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AnimalEditHuntingPeriodModal
      v-if="huntingPeriodToEdit"
      :animalsStorePath="animalsStorePath"
      :huntingPeriod="huntingPeriodToEdit"
      @cancelled="closeEditHuntingPeriodModal"
      @succeeded="editHuntingPeriodModalUpdatedDates"
    />

    <AnimalDeleteModal
      v-if="showDeleteAnimalModal"
      :animalsStorePath="animalsStorePath"
      :animal="selectedAnimal"
      @cancelled="closeDeleteAnimalModal"
      @succeeded="animalDeleted"
    />

    <AnimalEditDistrictModal
      v-if="animalDistrictToEdit"
      :animalsStorePath="animalsStorePath"
      :animalDistrict="animalDistrictToEdit"
      @cancelled="closeEditAnimalDistrictModal"
      @succeeded="closeEditAnimalDistrictModal"
    />
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'
import dayjs from 'dayjs'
import { v4 as uuidv4 } from 'uuid'

import {
  SELECT_ANIMAL_WITH_ID,
  LOAD_ANIMALS,
  GET_LOAD_RESULT,
  GET_SELECTED_ANIMAL,
  SAVE_ANIMAL,
  SAVE_ANIMAL_LOCALIZATION,
  DELETE_ANIMAL_LOCALIZATION,
  SAVE_ANIMAL_PERIOD,
  DELETE_ANIMAL_PERIOD,
  UPLOAD_ICON_IMAGE
} from '@/modules/animals/animalsStore'

import AnimalEditHuntingPeriodModal from './AnimalEditHuntingPeriodModal.vue'
import AnimalDeleteModal from './AnimalDeleteModal'
import AnimalEditDistrictModal from './AnimalEditDistrictModal.vue'

export default {
  name: 'AnimalDetails',
  components: {
    Spinner,
    AnimalEditHuntingPeriodModal,
    AnimalDeleteModal,
    AnimalEditDistrictModal
  },

  props: {
    animalsStorePath: {
      type: String,
      required: true
    },

    animalId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      animalCopy: null,
      allCountryCodesCopy: [],
      allLanguageCodesCopy: [],
      selectedLanguageCode: null,
      newCountryCode: null,
      newLanguageCode: null,
      validationErrors: null,
      isSaving: false,
      saveError: null,
      showDeleteAnimalModal: false,
      periodsCountryCodes: [],
      selectedHuntingPeriodsCountryCode: null,
      selectedHuntingPeriodId: null,
      huntingPeriodToEdit: null,
      animalDistrictToEdit: null,
      newPeriodsCountryCode: null,
      iconLoadFailed: false,
      iconIsLoaded: false,
      iconUpdateTimestamp: new Date(),
      isUploadingIconImage: false,
      iconUploadError: null
    }
  },

  mounted() {
    const setSelectedAnimalIdAction = `${this.animalsStorePath}/${SELECT_ANIMAL_WITH_ID}`
    this.$store.dispatch(setSelectedAnimalIdAction, { animalId: this.animalId })

    this.resetUI()
  },

  beforeRouteUpdate(to, from, next) {
    this.handleAboutToLeave(next)
  },

  beforeRouteLeave(to, from, next) {
    this.handleAboutToLeave(next)
  },

  methods: {
    resetUI() {
      if (!this.selectedAnimal) {
        this.animalCopy = null
        return
      }

      this.animalCopy = JSON.parse(JSON.stringify(this.selectedAnimal))

      this.iconIsLoaded = false
      this.iconLoadFailed = false
      this.isUploadingIconImage = false
      this.iconUploadError = null

      if (!this.animalCopy.countryCodes) {
        this.animalCopy.countryCodes = []
      }

      this.allCountryCodesCopy = [...this.allCountryCodes]
      this.allLanguageCodesCopy = [...this.allLanguageCodes]

      var periodsCountryCodes = []
      if (this.animalCopy.periods) {
        this.animalCopy.periods.forEach(period => {
          if (!periodsCountryCodes.includes(period.countryCode)) {
            periodsCountryCodes.push(period.countryCode)
          }
        })

        periodsCountryCodes.sort()
      }

      this.periodsCountryCodes = periodsCountryCodes

      if (this.animalCopy.localizations.length > 0) {
        this.selectedLanguageCode = this.animalCopy.localizations[0].languageCode
      } else {
        this.selectedLanguageCode = null
      }

      if (periodsCountryCodes.length > 0) {
        this.selectedHuntingPeriodsCountryCode = periodsCountryCodes[0]
      } else {
        this.selectedHuntingPeriodsCountryCode = null
      }
      this.selectedHuntingPeriodId = null

      this.validationErrors = null
    },

    handleAboutToLeave(next) {
      if (!this.isDirty) {
        next()
        return
      }

      const answer = window.confirm('Do you really want to leave? You have unsaved changes!')

      if (answer === true) {
        next()
      }
    },

    languageCodeClicked(languageCode) {
      this.selectedLanguageCode = languageCode
    },

    isSelectedLanguageCode(languageCode) {
      return languageCode === this.selectedLanguageCode
    },

    isValidName(name) {
      if (!name) {
        return false
      }

      return name.trim().length > 0
    },

    addCountryCode() {
      const countryCode = this.newCountryCode.trim().toUpperCase()

      if (this.allCountryCodesCopy.includes(countryCode)) {
        return
      }

      this.allCountryCodesCopy.push(countryCode)
      this.animalCopy.countryCodes.push(countryCode)

      this.newCountryCode = null
    },

    addLanguageCode() {
      const languageCode = this.newLanguageCode.trim().toLowerCase()

      if (
        this.animalCopy.localizations.find(
          localization => localization.languageCode === languageCode
        )
      ) {
        return
      }

      this.allLanguageCodesCopy.push(languageCode)
      this.animalCopy.localizations.push({
        languageCode,
        name: null
      })

      this.newLanguageCode = null
      this.selectedLanguageCode = languageCode
    },

    addLocalization() {
      if (!this.selectedLanguageCode) {
        return
      }

      this.animalCopy.localizations.push({
        languageCode: this.selectedLanguageCode,
        name: null
      })
    },

    removeLocalization() {
      if (!this.selectedLanguageCode) {
        return
      }

      this.animalCopy.localizations = this.animalCopy.localizations.filter(
        localization => localization.languageCode !== this.selectedLanguageCode
      )
    },

    async saveAnimal() {
      this.updateValidationErrors()

      if (this.hasValidationErrors) {
        return
      }

      this.isSaving = true
      this.saveError = null

      try {
        if (this.animalCopy.countryFilterEnabled === false) {
          this.animalCopy.countryCodes = []
        }

        const saveAnimalAction = `${this.animalsStorePath}/${SAVE_ANIMAL}`
        await this.$store.dispatch(saveAnimalAction, {
          animal: this.animalCopy
        })

        if (this.animalCopy.localizations) {
          const saveAnimalLocalizationAction = `${this.animalsStorePath}/${SAVE_ANIMAL_LOCALIZATION}`

          for (let index = 0; index < this.animalCopy.localizations.length; index++) {
            const animalLocalization = this.animalCopy.localizations[index]
            await this.$store.dispatch(saveAnimalLocalizationAction, {
              animalId: this.animalCopy.animalId,
              animalLocalization
            })
          }
        }

        if (this.removedLocalizationLanguageCodes) {
          const deleteAnimalLocalizationAction = `${this.animalsStorePath}/${DELETE_ANIMAL_LOCALIZATION}`

          for (let index = 0; index < this.removedLocalizationLanguageCodes.length; index++) {
            const languageCode = this.removedLocalizationLanguageCodes[index]
            await this.$store.dispatch(deleteAnimalLocalizationAction, {
              animalId: this.animalCopy.animalId,
              languageCode
            })
          }
        }

        const removedCountryCodes = this.selectedAnimal.countryCodes.filter(
          countryCode => !this.animalCopy.countryCodes.includes(countryCode)
        )
        if (removedCountryCodes.length > 0) {
          const deletePeriodAction = `${this.animalsStorePath}/${DELETE_ANIMAL_PERIOD}`

          // Remove all periods for the removed country codes
          for (let index = 0; index < removedCountryCodes.length; index++) {
            const countryCode = removedCountryCodes[index]
            const periodIdsForCountryCode = this.selectedAnimal.periods
              .filter(period => period.countryCode === countryCode)
              .map(period => period.id)

            for (let index2 = 0; index2 < periodIdsForCountryCode.length; index2++) {
              const animalPeriodId = periodIdsForCountryCode[index2]
              await this.$store.dispatch(deletePeriodAction, { animalPeriodId })
            }
          }
        }

        const saveAnimalPeriodAction = `${this.animalsStorePath}/${SAVE_ANIMAL_PERIOD}`
        for (let index = 0; index < this.animalCopy.periods.length; index++) {
          const animalPeriod = this.animalCopy.periods[index]

          await this.$store.dispatch(saveAnimalPeriodAction, {
            animalId: this.animalCopy.animalId,
            animalPeriod
          })
        }

        if (this.removedHuntingPeriods) {
          const deletePeriodAction = `${this.animalsStorePath}/${DELETE_ANIMAL_PERIOD}`

          for (let index = 0; index < this.removedHuntingPeriods.length; index++) {
            const animalPeriod = this.removedHuntingPeriods[index]
            await this.$store.dispatch(deletePeriodAction, { animalPeriodId: animalPeriod.id })
          }
        }

        this.reloadAnimals()
      } catch (error) {
        this.saveError = error || 'Something went wrong.'
      } finally {
        this.isSaving = false
      }
    },

    updateValidationErrors() {
      var validationErrors = []

      if (!this.isValidName(this.animalCopy.name)) {
        validationErrors.push('Name (English) must be set.')
      }

      this.animalCopy.localizations.forEach(localization => {
        if (!this.isValidName(localization.name)) {
          validationErrors.push(`Name must be set for localization ${localization.languageCode}.`)
        }
      })

      this.validationErrors = validationErrors
    },

    reloadAnimals() {
      const loadAnimalsAction = `${this.animalsStorePath}/${LOAD_ANIMALS}`
      this.$store.dispatch(loadAnimalsAction)
    },

    deleteAnimal() {
      this.showDeleteAnimalModal = true
    },

    closeDeleteAnimalModal() {
      this.showDeleteAnimalModal = false
    },

    animalDeleted() {
      this.showDeleteAnimalModal = false

      this.reloadAnimals()
      this.$router.replace({ name: 'animalsSearch', query: this.$route.query })
    },

    selectHuntingPeriodsCountryCode(countryCode) {
      this.selectedHuntingPeriodsCountryCode = countryCode
    },

    huntingPeriodClicked(huntingPeriod) {
      this.selectedHuntingPeriodId = huntingPeriod.id || huntingPeriod.tempId
    },

    isSelectedHuntingPeriodCode(huntingPeriod) {
      return (
        this.selectedHuntingPeriodId !== null &&
        (this.selectedHuntingPeriodId === huntingPeriod.id ||
          this.selectedHuntingPeriodId === huntingPeriod.tempId)
      )
    },

    addHuntingPeriod() {
      if (!this.selectedHuntingPeriodsCountryCode) {
        return
      }

      const now = dayjs(new Date()).format('YYYY-MM-DD')

      this.huntingPeriodToEdit = {
        tempId: uuidv4(),
        animalId: this.animalId,
        countryCode: this.selectedHuntingPeriodsCountryCode,
        districtIds: [],
        from: now,
        to: now
      }
    },

    editHuntingPeriod(huntingPeriod) {
      this.huntingPeriodToEdit = huntingPeriod
    },

    closeEditHuntingPeriodModal() {
      this.huntingPeriodToEdit = null
    },

    editHuntingPeriodModalUpdatedDates() {
      if (
        !this.animalCopy.periods.find(
          huntingPeriodToEdit => huntingPeriodToEdit.tempId === this.huntingPeriodToEdit.tempId
        )
      ) {
        // The edited period is a new period
        this.animalCopy.periods.push(this.huntingPeriodToEdit)
        this.selectedHuntingPeriodId = this.huntingPeriodToEdit.tempId
      }

      this.closeEditHuntingPeriodModal()
    },

    removeHuntingPeriod(huntingPeriod) {
      let huntingPeriodId = huntingPeriod.id || huntingPeriod.tempId

      this.animalCopy.periods = this.animalCopy.periods.filter(
        period => (period.id || period.tempId) !== huntingPeriodId
      )
    },

    selectAllPeriodDistricts(huntingPeriod) {
      if (!this.selectableDistricts) {
        return
      }

      huntingPeriod.districtIds = this.selectableDistricts.map(district => district.id)
    },

    deselectAllPeriodDistricts(huntingPeriod) {
      huntingPeriod.districtIds = []
    },

    addDistrict() {
      this.animalDistrictToEdit = {
        countryCode: this.selectedHuntingPeriodsCountryCode
      }
    },

    editDistrict(district) {
      this.animalDistrictToEdit = district
    },

    addPeriodsCountryCode() {
      const countryCode = this.newPeriodsCountryCode.trim().toUpperCase()

      if (this.periodsCountryCodes.includes(countryCode)) {
        return
      }

      this.periodsCountryCodes.push(countryCode)

      this.newPeriodsCountryCode = null
    },

    closeEditAnimalDistrictModal() {
      this.animalDistrictToEdit = null
    },

    async uploadIconImage(files) {
      if (files.length === 0) {
        return
      }

      const dataFile = files[0]
      const uploadIconImageAction = `${this.animalsStorePath}/${UPLOAD_ICON_IMAGE}`

      this.isUploadingIconImage = true
      this.iconUploadError = null
      try {
        const iconURL = await this.$store.dispatch(uploadIconImageAction, { dataFile })

        this.animalCopy.iconURL = iconURL

        // Hack to trigger reload of image
        this.iconUpdateTimestamp = new Date()

        this.iconIsLoaded = false
        this.iconLoadFailed = false
      } catch (error) {
        this.iconUploadError = error || 'Something went wrong.'
      } finally {
        this.isUploadingIconImage = false
      }
    },

    onIconLoaded() {
      this.iconLoadFailed = false
      this.iconIsLoaded = true
    },

    onIconLoadError() {
      this.iconLoadFailed = true
    }
  },

  computed: {
    selectedAnimal() {
      const getSelectedAnimal = `${this.animalsStorePath}/${GET_SELECTED_ANIMAL}`
      return this.$store.getters[getSelectedAnimal]
    },

    allCountryCodes() {
      const getAnimalData = `${this.animalsStorePath}/${GET_LOAD_RESULT}`
      const animalData = this.$store.getters[getAnimalData]

      if (!animalData || !animalData.allCountryCodes) {
        return []
      }

      return animalData.allCountryCodes
    },

    sortedCountryCodes() {
      if (!this.allCountryCodesCopy) {
        return []
      }

      var sortedCountryCodes = [...this.allCountryCodesCopy]
      sortedCountryCodes.sort()

      return sortedCountryCodes
    },

    allLanguageCodes() {
      const getAnimalData = `${this.animalsStorePath}/${GET_LOAD_RESULT}`
      const animalData = this.$store.getters[getAnimalData]

      if (!animalData || !animalData.allLanguageCodes) {
        return []
      }

      return animalData.allLanguageCodes
    },

    sortedLanguageCodes() {
      if (!this.allLanguageCodesCopy) {
        return []
      }

      var sortedLanguageCodes = [...this.allLanguageCodesCopy]
      sortedLanguageCodes.sort()

      return sortedLanguageCodes
    },

    isNameValid() {
      return this.isValidName(this.animalCopy.name)
    },

    allDistricts() {
      const getAnimalData = `${this.animalsStorePath}/${GET_LOAD_RESULT}`
      const animalData = this.$store.getters[getAnimalData]

      if (!animalData || !animalData.allDistricts) {
        return []
      }

      return animalData.allDistricts
    },

    selectedLocalization() {
      if (!this.selectedLanguageCode) {
        return null
      }

      if (!this.animalCopy) {
        return null
      }

      return this.animalCopy.localizations.find(
        localization => localization.languageCode === this.selectedLanguageCode
      )
    },

    isSelectedLocalizationNameValid() {
      if (!this.selectedLocalization) {
        return false
      }

      return this.isValidName(this.selectedLocalization.name)
    },

    isNewCountryCodeValid() {
      if (!this.newCountryCode) {
        return false
      }

      return this.newCountryCode.trim().length > 1
    },

    isNewLanguageCodeValid() {
      if (!this.newLanguageCode) {
        return false
      }

      return this.newLanguageCode.trim().length > 1
    },

    hasValidationErrors() {
      if (!this.validationErrors) {
        return false
      }

      return this.validationErrors.length > 0
    },

    removedLocalizationLanguageCodes() {
      if (!this.selectedAnimal || !this.selectedAnimal.localizations) {
        return null
      }

      if (!this.animalCopy || !this.animalCopy.localizations) {
        return null
      }

      const selectedAnimalLanguageCodes = this.selectedAnimal.localizations.map(
        localization => localization.languageCode
      )
      const animalCopyLanguageCodes = this.animalCopy.localizations.map(
        localization => localization.languageCode
      )

      const removedLanguageCodes = selectedAnimalLanguageCodes.filter(
        languageCode => !animalCopyLanguageCodes.includes(languageCode)
      )

      return removedLanguageCodes
    },

    visibleHuntingPeriods() {
      if (!this.animalCopy || !this.animalCopy.periods || !this.selectedHuntingPeriodsCountryCode) {
        return []
      }

      var sortedPeriods = this.animalCopy.periods.filter(
        period => period.countryCode === this.selectedHuntingPeriodsCountryCode
      )
      sortedPeriods.sort((period1, period2) => {
        if (period1.from < period2.from) {
          return -1
        }
        if (period1.from > period2.from) {
          return 1
        }

        return 0
      })

      return sortedPeriods
    },

    selectedHuntingPeriod() {
      if (!this.selectedHuntingPeriodId) {
        return null
      }

      return this.visibleHuntingPeriods.find(
        huntingPeriod =>
          huntingPeriod.id === this.selectedHuntingPeriodId ||
          huntingPeriod.tempId === this.selectedHuntingPeriodId
      )
    },

    removedHuntingPeriods() {
      if (!this.selectedAnimal || !this.selectedAnimal.periods) {
        return null
      }

      if (!this.animalCopy || !this.animalCopy.periods) {
        return null
      }

      const animalCopyHuntingPeriodIds = this.animalCopy.periods
        .filter(period => period.id)
        .map(period => period.id)

      const removedHuntingPeriods = this.selectedAnimal.periods.filter(
        period => !animalCopyHuntingPeriodIds.includes(period.id)
      )

      return removedHuntingPeriods
    },

    selectableDistricts() {
      if (!this.selectedHuntingPeriodsCountryCode) {
        return null
      }

      var filteredDistricts = this.allDistricts.filter(
        district => district.countryCode === this.selectedHuntingPeriodsCountryCode
      )
      filteredDistricts.sort((district1, district2) =>
        district1.name.localeCompare(district2.name, 'sv')
      )

      return filteredDistricts
    },

    isNewPeriodsCountryCodeValid() {
      if (!this.newPeriodsCountryCode) {
        return false
      }

      return this.newPeriodsCountryCode.trim().length > 1
    },

    iconImageURL() {
      if (!this.animalCopy || !this.animalCopy.iconURL) {
        return ''
      }

      return `${this.animalCopy.iconURL}?u=${this.iconUpdateTimestamp}`
    },

    isDirty() {
      return JSON.stringify(this.selectedAnimal) !== JSON.stringify(this.animalCopy)
    }
  },

  watch: {
    selectedAnimal() {
      this.resetUI()
    },

    periodsCountryCodes() {
      if (!this.periodsCountryCodes || this.periodsCountryCodes.length === 0) {
        this.selectedHuntingPeriodsCountryCode = null
        return
      }

      if (
        !this.selectedHuntingPeriodsCountryCode ||
        !this.periodsCountryCodes.find(
          countryCode => countryCode === this.selectedHuntingPeriodsCountryCode
        )
      ) {
        this.selectedHuntingPeriodsCountryCode = this.periodsCountryCodes[0]
      }
    },

    visibleHuntingPeriods() {
      if (this.selectedHuntingPeriod) {
        return
      }

      if (this.visibleHuntingPeriods && this.visibleHuntingPeriods.length > 0) {
        let firstHuntingPeriod = this.visibleHuntingPeriods[0]
        this.selectedHuntingPeriodId = firstHuntingPeriod.id || firstHuntingPeriod.tempId
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/style.scss';

.main {
  padding: 1em;
}

.control {
  figure {
    margin-top: 0.5em;

    img {
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 10px;
      border: 1px solid $grey-lightest;
      object-fit: cover;
    }
  }
}

.localizations-sidebar {
  padding: 1em;
  border-radius: 10px;
}

.inline-list {
  min-width: 200px;
  overflow: hidden;
  border-radius: 6px;
}

.inline-list-details {
  margin-left: 1em;

  .button {
    margin-top: 1em;
  }
}

.add-country-code {
  margin-top: 1em;
  width: 200px;
}

.top-button-row {
  padding-left: 1em;
  padding-right: 1em;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  border-bottom: solid 1px $grey-lightest;

  .notification {
    margin-top: 0.5em;
  }
}

.top-button-container {
  display: flex;
  justify-content: space-between;
}

.add-localization {
  display: flex;
  justify-content: center;
  padding: 2em;
}

.add-hunting-period {
  margin-top: 1em;
}

.hunting-period-details {
  margin-left: 1em;

  .label {
    .hunting-period {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .districts-container {
    a {
      text-decoration-line: underline;
      padding-right: 1em;
    }

    .district-checkbox {
      margin-top: 0.25em;

      .district-name {
        margin-left: 0.5em;
      }

      a {
        text-decoration: none;
      }
    }
  }

  .add-district {
    margin-top: 1em;
  }
}

.image-container {
  position: relative;

  .iconImage {
    width: 128px;
    height: 128px;
    max-width: 128px;
    max-height: 128px;
    min-width: 128px;
    min-height: 128px;
  }

  .image-scroll-area {
    overflow: auto;
  }

  .overlay {
    position: absolute;
    width: 128px;
    height: 128px;
    left: 0;
    top: 0;
    right: 0;
    bottom: 6px;
    display: flex;
    align-content: center;
    align-items: center;

    background-color: hsla(0, 0, 0, 0.5);
  }
}

.image-missing {
  margin-bottom: 1em;
}

.file {
  .file-label {
    margin-right: 4px;
  }
}

.file.is-primary .file-input[disabled] ~ .file-cta:hover,
.file.is-primary .file-input[disabled] ~ .file-cta.is-hovered,
.file.is-primary .file-input[disabled] ~ .file-cta:focus,
.file.is-primary .file-input[disabled] ~ .file-cta.is-focused,
.file.is-primary .file-input[disabled] ~ .file-cta:active,
.file.is-primary .file-input[disabled] ~ .file-cta.is-active {
  background-color: whitesmoke;
  border-color: whitesmoke;
  color: #7a7a7a;
}
.file-input[disabled] ~ .file-cta,
.file-input[disabled] ~ .file-name {
  background-color: whitesmoke;
  border-color: whitesmoke;
  color: #7a7a7a;
}

.file-input[disabled] ~ .file-cta,
.file-input[disabled] ~ .file-name,
.file-input[disabled] ~ .file-cta .file-label {
  cursor: not-allowed;
}
</style>
